import { useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Pencil, Trash2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { LEVELS, ROWS, POS, locId, breakdown } from "./shelfShared";

export function SlotDetailModal({ rack, racks, location, slot, onClose, onEdit, onClear, onMoved }) {
  const [moveOpen, setMoveOpen] = useState(false);
  const [toRackId, setToRackId] = useState(rack.id);
  const [toLoc, setToLoc] = useState("");
  const [moving, setMoving] = useState(false);

  const toRack = racks.find((r) => r.id === toRackId);
  const emptyLocations = useMemo(() => {
    if (!toRack) return [];
    const all = [];
    LEVELS.forEach((l) => ROWS.forEach((r) => POS.forEach((p) => all.push(locId(toRack.code, l, r, p)))));
    return all.filter((loc) => !(toRack.slots || {})[loc]);
  }, [toRack]);

  const doMove = async () => {
    if (!toLoc) return;
    setMoving(true);
    try {
      await api.post("/racks/move", { from_rack_id: rack.id, from_location: location, to_rack_id: toRackId, to_location: toLoc });
      toast.success("Inventory moved");
      onMoved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setMoving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="slot-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{slot.component_name}</DialogTitle>
          <DialogDescription>{location} · {slot.type_name}{slot.design_name ? ` · ${slot.design_name}` : ""}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2 text-center">
          {breakdown(slot.units, slot.units_per_case, slot.units_per_pallet).reverse().map((b, i) => (
            <div key={`${b}-${i}`} className="rounded-sm border border-slate-200 bg-slate-50 py-3 text-sm font-semibold text-slate-800">{b}</div>
          ))}
        </div>

        {!moveOpen ? (
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="rounded-sm" onClick={onEdit} data-testid="slot-edit-button"><Pencil className="mr-1 h-4 w-4" /> Edit Quantity</Button>
            <Button variant="outline" className="rounded-sm" onClick={() => setMoveOpen(true)} data-testid="slot-move-button"><ArrowRightLeft className="mr-1 h-4 w-4" /> Move</Button>
            <Button variant="outline" className="rounded-sm border-red-200 text-red-600 hover:bg-red-50" onClick={onClear} data-testid="slot-clear-button"><Trash2 className="mr-1 h-4 w-4" /> Clear Location</Button>
          </DialogFooter>
        ) : (
          <div className="space-y-3 rounded-sm border border-slate-200 p-3" data-testid="move-panel">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Move to</p>
            <div className="grid grid-cols-2 gap-2">
              <Select value={toRackId} onValueChange={(v) => { setToRackId(v); setToLoc(""); }}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="move-rack-select"><SelectValue /></SelectTrigger>
                <SelectContent>{racks.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={toLoc} onValueChange={setToLoc}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="move-location-select"><SelectValue placeholder="Position" /></SelectTrigger>
                <SelectContent className="max-h-64">{emptyLocations.map((loc) => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setMoveOpen(false)}>Cancel</Button>
              <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" disabled={moving || !toLoc} onClick={doMove} data-testid="move-confirm">
                {moving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Move here"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
